import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardBody, CardHeader, Chip, Tabs, Tab, Divider, Button, Progress } from '@nextui-org/react';
import {
  Trophy,
  MapPin,
  Calendar,
  Users,
  Tv,
  Coins,
  Globe2,
  Flag,
  Crown,
  History,
  Star,
  ChevronRight,
  Info,
} from 'lucide-react';

const ACCENT = '#3fced1';

interface TeamEntry {
  flag: string;
  nation: string;
  player1: string;
  player2: string;
  seeded?: boolean;
  titleHolder?: boolean;
  prevWinner?: number[];
}

interface PrizeRow {
  stage: string;
  per: string;
  totalNote?: string;
}

interface GroupEntry {
  group: string;
  teams: { flag: string; nation: string }[];
}

interface ScheduleSession {
  day: string;
  date: string;
  time: string;
  title: string;
  stage: string;
}

interface PastWinner {
  year: number;
  champion: string;
  flag: string;
  players: string;
  runnerUp: string;
  runnerUpFlag: string;
  score: string;
}

const TOURNAMENT_INFO = {
  fullName: 'PDC World Cup of Darts 2026',
  edition: '16. Auflage',
  dates: '11.–14. Juni 2026',
  venue: 'Eissporthalle',
  city: 'Frankfurt am Main',
  country: 'Deutschland',
  organizer: 'Professional Darts Corporation (PDC)',
  format: 'Doppel · 40 Nationen',
  prizeMoney: '£500.000',
  winnerShare: '£50.000 pro Spieler',
  broadcaster: 'DAZN · Pluto TV (kostenlos)',
  titleHolder: 'Nordirland',
  titleHolderPlayers: 'Josh Rock & Daryl Gurney',
  recordChampion: 'England (6 Titel)',
};

const PRIZE_BREAKDOWN: PrizeRow[] = [
  { stage: 'Sieger', per: '£50.000', totalNote: '£100.000 / Team' },
  { stage: 'Finalist', per: '£24.000', totalNote: '£48.000 / Team' },
  { stage: 'Halbfinalist', per: '£15.000', totalNote: '£30.000 / Team' },
  { stage: 'Viertelfinale', per: '£10.000', totalNote: '£20.000 / Team' },
  { stage: 'Achtelfinale', per: '£5.000', totalNote: '£10.000 / Team' },
  { stage: 'Gruppe – Platz 2', per: '£3.000', totalNote: '£6.000 / Team' },
  { stage: 'Gruppe – Platz 3', per: '£2.500', totalNote: '£5.000 / Team' },
];

const FORMAT_RULES = [
  { label: 'Gruppenspiele', value: 'Best of 7 Legs' },
  { label: 'Achtelfinale', value: 'Best of 15 Legs' },
  { label: 'Viertelfinale', value: 'Best of 15 Legs' },
  { label: 'Halbfinale', value: 'Best of 15 Legs' },
  { label: 'Finale', value: 'Best of 19 Legs' },
];

const SEEDED_TEAMS: TeamEntry[] = [
  {
    flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
    nation: 'England',
    player1: 'Luke Humphries',
    player2: 'Michael Smith',
    seeded: true,
    prevWinner: [2012, 2013, 2014, 2015, 2016, 2024],
  },
  {
    flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
    nation: 'Wales',
    player1: 'Gerwyn Price',
    player2: 'Jonny Clayton',
    seeded: true,
    prevWinner: [2020],
  },
  {
    flag: '🇳🇱',
    nation: 'Niederlande',
    player1: 'Michael van Gerwen',
    player2: 'Danny Noppert',
    seeded: true,
    prevWinner: [2010, 2014, 2017, 2018],
  },
  {
    flag: '🇬🇧',
    nation: 'Nordirland',
    player1: 'Josh Rock',
    player2: 'Daryl Gurney',
    seeded: true,
    titleHolder: true,
    prevWinner: [2025],
  },
];

const NOTABLE_TEAMS: TeamEntry[] = [
  { flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', nation: 'Schottland', player1: 'Gary Anderson', player2: 'Peter Wright', prevWinner: [2021] },
  { flag: '🇧🇪', nation: 'Belgien', player1: 'Dimitri Van den Bergh', player2: 'Kim Huybrechts', prevWinner: [2019] },
  { flag: '🇩🇪', nation: 'Deutschland', player1: 'Martin Schindler', player2: 'Ricardo Pietreczko' },
  { flag: '🇦🇹', nation: 'Österreich', player1: 'Mensur Suljović', player2: 'Rusty-Jake Rodriguez' },
  { flag: '🇨🇭', nation: 'Schweiz', player1: 'Stefan Bellmont', player2: 'Joel Burri' },
  { flag: '🇦🇺', nation: 'Australien', player1: 'Damon Heta', player2: 'Simon Whitlock' },
  { flag: '🇨🇦', nation: 'Kanada', player1: 'Matt Campbell', player2: 'Jeff Smith' },
  { flag: '🇮🇪', nation: 'Irland', player1: 'William O\'Connor', player2: 'Keane Barry' },
  { flag: '🇸🇪', nation: 'Schweden', player1: 'Jeffrey de Graaf', player2: 'Oskar Lukasiak' },
  { flag: '🇩🇰', nation: 'Dänemark', player1: 'Andreas Harrysson', player2: 'Vladimir Andersen' },
  { flag: '🇵🇱', nation: 'Polen', player1: 'Krzysztof Ratajski', player2: 'Sebastian Białecki' },
  { flag: '🇨🇿', nation: 'Tschechien', player1: 'Karel Sedláček', player2: 'Adam Gawlas' },
  { flag: '🇯🇵', nation: 'Japan', player1: 'Tomoya Goto', player2: 'Haruki Muramatsu' },
  { flag: '🇭🇰', nation: 'Hongkong', player1: 'Lok Yin Lee', player2: 'Man Lok Leung' },
  { flag: '🇿🇦', nation: 'Südafrika', player1: 'Devon Petersen', player2: 'Cameron Carolissen' },
  { flag: '🇺🇸', nation: 'USA', player1: 'Danny Lauby', player2: 'Jules van Dongen' },
];

const ALL_TEAMS: TeamEntry[] = [...SEEDED_TEAMS, ...NOTABLE_TEAMS];

const GROUP_STAGE: GroupEntry[] = [
  { group: 'Gruppe A', teams: [{ flag: '🇧🇪', nation: 'Belgien' }, { flag: '🇯🇵', nation: 'Japan' }, { flag: '🇿🇦', nation: 'Südafrika' }] },
  { group: 'Gruppe B', teams: [{ flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', nation: 'Schottland' }, { flag: '🇨🇿', nation: 'Tschechien' }, { flag: '🇺🇸', nation: 'USA' }] },
  { group: 'Gruppe C', teams: [{ flag: '🇩🇪', nation: 'Deutschland' }, { flag: '🇨🇭', nation: 'Schweiz' }, { flag: '🇭🇰', nation: 'Hongkong' }] },
  { group: 'Gruppe D', teams: [{ flag: '🇦🇺', nation: 'Australien' }, { flag: '🇦🇹', nation: 'Österreich' }, { flag: '🇨🇦', nation: 'Kanada' }] },
  { group: 'Gruppe E', teams: [{ flag: '🇮🇪', nation: 'Irland' }, { flag: '🇸🇪', nation: 'Schweden' }, { flag: '🇩🇰', nation: 'Dänemark' }] },
  { group: 'Gruppe F', teams: [{ flag: '🇵🇱', nation: 'Polen' }, { flag: '🇫🇮', nation: 'Finnland' }, { flag: '🇳🇿', nation: 'Neuseeland' }] },
  { group: 'Gruppe G', teams: [{ flag: '🇭🇺', nation: 'Ungarn' }, { flag: '🇪🇸', nation: 'Spanien' }, { flag: '🇵🇹', nation: 'Portugal' }] },
  { group: 'Gruppe H', teams: [{ flag: '🇮🇹', nation: 'Italien' }, { flag: '🇫🇷', nation: 'Frankreich' }, { flag: '🇱🇹', nation: 'Litauen' }] },
  { group: 'Gruppe I', teams: [{ flag: '🇧🇬', nation: 'Bulgarien' }, { flag: '🇷🇴', nation: 'Rumänien' }, { flag: '🇧🇦', nation: 'Bosnien-H.' }] },
  { group: 'Gruppe J', teams: [{ flag: '🇨🇳', nation: 'China' }, { flag: '🇵🇭', nation: 'Philippinen' }, { flag: '🇮🇩', nation: 'Indonesien' }] },
  { group: 'Gruppe K', teams: [{ flag: '🇲🇾', nation: 'Malaysia' }, { flag: '🇸🇬', nation: 'Singapur' }, { flag: '🇮🇳', nation: 'Indien' }] },
  { group: 'Gruppe L', teams: [{ flag: '🇳🇬', nation: 'Nigeria' }, { flag: '🇪🇬', nation: 'Ägypten' }, { flag: '🇬🇮', nation: 'Gibraltar' }] },
];

const SCHEDULE: ScheduleSession[] = [
  { day: 'Donnerstag', date: '11.06.2026', time: '13:00', title: 'Session 1 · Gruppenphase', stage: 'Erste Runde' },
  { day: 'Donnerstag', date: '11.06.2026', time: '19:00', title: 'Session 2 · Gruppenphase', stage: 'Erste Runde' },
  { day: 'Freitag', date: '12.06.2026', time: '13:00', title: 'Session 3 · Gruppenphase Abschluss', stage: 'Letzte Spiele Gruppe' },
  { day: 'Freitag', date: '12.06.2026', time: '19:00', title: 'Session 4 · Achtelfinale', stage: 'Round of 16' },
  { day: 'Samstag', date: '13.06.2026', time: '13:00', title: 'Session 5 · Viertelfinale', stage: 'Quarter Finals' },
  { day: 'Samstag', date: '13.06.2026', time: '19:00', title: 'Session 6 · Halbfinale', stage: 'Semi Finals' },
  { day: 'Sonntag', date: '14.06.2026', time: '19:00', title: 'Finale', stage: 'Final · Best of 19' },
];

const PAST_WINNERS: PastWinner[] = [
  { year: 2025, champion: 'Nordirland', flag: '🇬🇧', players: 'Josh Rock & Daryl Gurney', runnerUp: 'Wales', runnerUpFlag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', score: '10:9' },
  { year: 2024, champion: 'England', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', players: 'Luke Humphries & Michael Smith', runnerUp: 'Österreich', runnerUpFlag: '🇦🇹', score: '10:8' },
  { year: 2023, champion: 'Wales', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', players: 'Gerwyn Price & Jonny Clayton', runnerUp: 'England', runnerUpFlag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', score: '10:8' },
  { year: 2022, champion: 'Australien', flag: '🇦🇺', players: 'Damon Heta & Simon Whitlock', runnerUp: 'Wales', runnerUpFlag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', score: '11:8' },
  { year: 2021, champion: 'Schottland', flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', players: 'Gary Anderson & Peter Wright', runnerUp: 'Österreich', runnerUpFlag: '🇦🇹', score: '3:1 (Best of 5)' },
  { year: 2020, champion: 'Wales', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', players: 'Gerwyn Price & Jonny Clayton', runnerUp: 'England', runnerUpFlag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', score: '3:2' },
  { year: 2019, champion: 'Schottland', flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', players: 'Gary Anderson & Peter Wright', runnerUp: 'Irland', runnerUpFlag: '🇮🇪', score: '3:2' },
  { year: 2018, champion: 'Niederlande', flag: '🇳🇱', players: 'Michael van Gerwen & Raymond van Barneveld', runnerUp: 'Schottland', runnerUpFlag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', score: '3:2' },
  { year: 2017, champion: 'Niederlande', flag: '🇳🇱', players: 'Michael van Gerwen & Raymond van Barneveld', runnerUp: 'England', runnerUpFlag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', score: '3:2' },
  { year: 2016, champion: 'England', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', players: 'Phil Taylor & Adrian Lewis', runnerUp: 'Niederlande', runnerUpFlag: '🇳🇱', score: '3:2' },
  { year: 2015, champion: 'England', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', players: 'Phil Taylor & Adrian Lewis', runnerUp: 'Schottland', runnerUpFlag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', score: '3:1' },
];

const RECORDS = [
  { label: 'Rekordsieger', value: 'England', sub: '6 Titel' },
  { label: 'Erfolgreichste Spieler', value: 'Phil Taylor / A. Lewis', sub: '4 Titel gemeinsam' },
  { label: 'Höchstes Preisgeld', value: '£500.000', sub: 'seit 2026' },
  { label: 'Gründungsjahr', value: '2010', sub: 'erste Austragung' },
  { label: 'Teilnehmende Nationen', value: '40', sub: 'seit 2023' },
  { label: 'Erstes Finale Best of 19', value: '2022', sub: 'aktueller Modus' },
];

const ROUND16_BRACKET = [
  ['🏴󠁧󠁢󠁥󠁮󠁧󠁿 England (1)', '🥇 Sieger Gruppe G'],
  ['🥇 Sieger Gruppe E', '🥇 Sieger Gruppe L'],
  ['🥇 Sieger Gruppe C', '🥇 Sieger Gruppe I'],
  ['🏴󠁧󠁢󠁷󠁬󠁳󠁿 Wales (4)', '🥇 Sieger Gruppe K'],
  ['🇬🇧 Nordirland (3)', '🥇 Sieger Gruppe J'],
  ['🥇 Sieger Gruppe D', '🥇 Sieger Gruppe H'],
  ['🥇 Sieger Gruppe B', '🥇 Sieger Gruppe F'],
  ['🇳🇱 Niederlande (2)', '🥇 Sieger Gruppe A'],
];

const StatItem: React.FC<{ icon: React.ReactNode; label: string; value: string; sub?: string }> = ({
  icon,
  label,
  value,
  sub,
}) => (
  <div className="flex items-start gap-3 p-4 rounded-2xl bg-zinc-900/60 border border-zinc-800">
    <div className="h-10 w-10 shrink-0 rounded-xl bg-[#3fced1]/10 text-[#3fced1] flex items-center justify-center">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className="text-base md:text-lg font-bold text-white leading-tight">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const SectionHeading: React.FC<{ icon: React.ReactNode; eyebrow: string; title: string; description?: string }> = ({
  icon,
  eyebrow,
  title,
  description,
}) => (
  <div className="mb-6 flex items-start gap-4">
    <div className="h-12 w-12 shrink-0 rounded-2xl bg-[#3fced1]/10 text-[#3fced1] flex items-center justify-center">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">{eyebrow}</p>
      <h2 className="text-2xl md:text-3xl font-extrabold text-white">{title}</h2>
      {description && <p className="text-sm text-gray-400 mt-1 max-w-2xl">{description}</p>}
    </div>
  </div>
);

const WorldCupOfDartsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('overview');

  const totalPrize = useMemo(() => '£500.000', []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>PDC World Cup of Darts 2026 · DartCloud</title>
        <meta
          name="description"
          content="Alle Infos zum PDC World Cup of Darts 2026 in Frankfurt: Teilnehmer, Draw, Bracket, Preisgeld, Spielplan, Sieger der Vorjahre."
        />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800 bg-gradient-to-b from-zinc-950 via-black to-black">
        {/* Subtle accent glow */}
        <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#3fced1]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-amber-500/5 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-16">
          {/* Top breadcrumb / kicker */}
          <div className="flex items-center gap-2 mb-6 text-xs text-gray-500 uppercase tracking-widest">
            <span>Turniere</span>
            <ChevronRight size={12} />
            <span>PDC Majors</span>
            <ChevronRight size={12} />
            <span className="text-[#3fced1]">World Cup of Darts</span>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-5">
            <Chip className="bg-[#3fced1] text-black font-bold" size="sm">
              PDC TEAM-WM
            </Chip>
            <Chip className="bg-zinc-900 text-white border border-zinc-800" size="sm">
              {TOURNAMENT_INFO.edition}
            </Chip>
            <Chip variant="bordered" className="border-amber-500/40 text-amber-300" size="sm">
              Major-Turnier
            </Chip>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05]">
            World Cup <span className="text-[#3fced1]">of Darts</span>
            <span className="block text-3xl md:text-5xl lg:text-6xl text-gray-400 font-extrabold mt-1">
              2026
            </span>
          </h1>

          <p className="text-base md:text-lg text-gray-300 max-w-3xl mt-5 leading-relaxed">
            Das einzige Doppel-Major der PDC: 40 Nationen, jeweils die zwei besten Spieler eines Landes –
            und am Ende ein neuer Weltmeister im Team-Format. Vom 11. bis 14. Juni 2026 in der
            Eissporthalle Frankfurt.
          </p>

          {/* Key facts grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8 max-w-4xl">
            <div className="rounded-2xl border border-[#3fced1]/30 bg-[#3fced1]/[0.06] p-4">
              <div className="flex items-center gap-2 text-[#3fced1] mb-1">
                <Calendar size={16} />
                <span className="text-[10px] uppercase tracking-widest font-bold">Termin</span>
              </div>
              <p className="text-sm md:text-base font-bold text-white">{TOURNAMENT_INFO.dates}</p>
            </div>

            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <MapPin size={16} />
                <span className="text-[10px] uppercase tracking-widest font-bold">Austragungsort</span>
              </div>
              <p className="text-sm md:text-base font-bold text-white leading-tight">
                {TOURNAMENT_INFO.venue}
              </p>
              <p className="text-xs text-gray-400">{TOURNAMENT_INFO.city}</p>
            </div>

            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4">
              <div className="flex items-center gap-2 text-amber-300 mb-1">
                <Coins size={16} />
                <span className="text-[10px] uppercase tracking-widest font-bold">Preisgeld</span>
              </div>
              <p className="text-sm md:text-base font-bold text-white">{totalPrize}</p>
              <p className="text-xs text-gray-400">£50.000 / Sieger-Spieler</p>
            </div>

            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Tv size={16} />
                <span className="text-[10px] uppercase tracking-widest font-bold">Übertragung</span>
              </div>
              <p className="text-sm md:text-base font-bold text-white leading-tight">DAZN</p>
              <p className="text-xs text-gray-400">Pluto TV (kostenlos)</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <Button
              className="bg-[#3fced1] text-black font-bold hover:bg-[#35b5b8]"
              endContent={<ChevronRight size={18} />}
              size="lg"
            >
              Spielplan ansehen
            </Button>
            <Button
              variant="bordered"
              className="border-zinc-700 text-white hover:bg-zinc-900"
              startContent={<Star size={16} />}
              size="lg"
            >
              Turnier merken
            </Button>
          </div>
        </div>

        {/* Bottom highlight strip */}
        <div className="relative border-t border-zinc-900 bg-black/40 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-gray-400">
              <Crown size={14} className="text-amber-400" />
              <span>
                <span className="text-gray-500">Titelverteidiger: </span>
                <span className="text-white font-bold">{TOURNAMENT_INFO.titleHolder}</span>
                <span className="text-gray-500"> · {TOURNAMENT_INFO.titleHolderPlayers}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Trophy size={14} className="text-amber-400" />
              <span>
                <span className="text-gray-500">Rekord: </span>
                <span className="text-white font-bold">{TOURNAMENT_INFO.recordChampion}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Users size={14} className="text-[#3fced1]" />
              <span>
                <span className="text-gray-500">Format: </span>
                <span className="text-white font-bold">{TOURNAMENT_INFO.format}</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatItem icon={<Users size={20} />} label="Nationen" value="40" sub="Doppel-Format" />
          <StatItem icon={<Coins size={20} />} label="Preisgeld" value={totalPrize} sub="Gesamt 2026" />
          <StatItem icon={<Crown size={20} />} label="Sieger-Anteil" value="£50.000" sub="pro Spieler" />
          <StatItem icon={<Trophy size={20} />} label="Rekordsieger" value="England" sub="6 Titel" />
          <StatItem icon={<Flag size={20} />} label="Titelverteidiger" value="Nordirland" sub="Rock & Gurney" />
          <StatItem icon={<History size={20} />} label="Auflage" value="16." sub="seit 2010" />
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <Tabs
          aria-label="Turnier-Informationen"
          selectedKey={activeTab}
          onSelectionChange={(key) => setActiveTab(String(key))}
          variant="underlined"
          color="primary"
          classNames={{
            tabList: 'gap-6 w-full overflow-x-auto border-b border-zinc-800',
            cursor: 'bg-[#3fced1]',
            tab: 'h-12 px-2',
            tabContent: 'text-gray-400 group-data-[selected=true]:text-white font-semibold',
          }}
        >
          <Tab key="overview" title="Überblick">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <Card className="bg-zinc-900/60 border border-zinc-800 lg:col-span-2">
                <CardHeader className="flex gap-3">
                  <div className="h-10 w-10 rounded-xl bg-[#3fced1]/10 text-[#3fced1] flex items-center justify-center">
                    <Info size={20} />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
                      Das Turnier
                    </p>
                    <h3 className="text-xl font-bold">Was ist der World Cup of Darts?</h3>
                  </div>
                </CardHeader>
                <CardBody className="pt-0 space-y-3 text-gray-300 text-sm leading-relaxed">
                  <p>
                    Der <strong className="text-white">PDC World Cup of Darts</strong> ist das einzige Major-Turnier
                    der Professional Darts Corporation, das ausschließlich im Doppel-Modus gespielt wird – daher auch
                    als <em className="text-[#3fced1]">"Team-WM"</em> bekannt.
                  </p>
                  <p>
                    Jede teilnehmende Nation wird durch ihre zwei besten Spieler aus der PDC Order of Merit vertreten.
                    2026 gehen <strong className="text-white">40 Nationen</strong> an den Start. Die Top 4 sind direkt
                    für das Achtelfinale gesetzt, die übrigen 36 spielen in zwölf 3er-Gruppen um die restlichen 12
                    Plätze.
                  </p>
                  <p>
                    Die <strong className="text-white">16. Auflage</strong> findet erstmals in der Eissporthalle in
                    Frankfurt statt – ein historisches Heimspiel für die deutschen Vertreter.
                  </p>
                </CardBody>
              </Card>

              <Card className="bg-zinc-900/60 border border-zinc-800">
                <CardHeader>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
                      Modus
                    </p>
                    <h3 className="text-xl font-bold">Spieldistanzen</h3>
                  </div>
                </CardHeader>
                <CardBody className="pt-0 space-y-2">
                  {FORMAT_RULES.map((rule, idx) => (
                    <div
                      key={rule.label}
                      className={`flex items-center justify-between rounded-xl px-3 py-2 ${
                        idx === FORMAT_RULES.length - 1
                          ? 'bg-[#3fced1]/10 border border-[#3fced1]/30'
                          : 'bg-zinc-950/60'
                      }`}
                    >
                      <span className="text-sm text-gray-300">{rule.label}</span>
                      <Chip
                        size="sm"
                        className={
                          idx === FORMAT_RULES.length - 1
                            ? 'bg-[#3fced1] text-black font-bold'
                            : 'bg-zinc-800 text-white'
                        }
                      >
                        {rule.value}
                      </Chip>
                    </div>
                  ))}
                </CardBody>
              </Card>

              <Card className="bg-zinc-900/60 border border-zinc-800 lg:col-span-3">
                <CardHeader>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
                      Rekorde & Wissenswertes
                    </p>
                    <h3 className="text-xl font-bold">Zahlen & Fakten</h3>
                  </div>
                </CardHeader>
                <CardBody className="pt-0">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {RECORDS.map((r) => (
                      <div
                        key={r.label}
                        className="rounded-xl p-3 bg-zinc-950/60 border border-zinc-800"
                      >
                        <p className="text-[10px] uppercase tracking-widest text-gray-500">{r.label}</p>
                        <p className="text-lg font-bold text-white">{r.value}</p>
                        <p className="text-xs text-gray-400">{r.sub}</p>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
          </Tab>

          <Tab key="teams" title={`Teilnehmer (${ALL_TEAMS.length}+)`}>
            <div className="mt-6 space-y-6">
              {/* Seeded */}
              <div>
                <SectionHeading
                  icon={<Crown size={22} />}
                  eyebrow="Top 4 · Gesetzte Nationen"
                  title="Direkt für das Achtelfinale qualifiziert"
                  description="Die vier bestplatzierten Nationen der kombinierten PDC Order of Merit überspringen die Gruppenphase."
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {SEEDED_TEAMS.map((t, idx) => (
                    <Card
                      key={t.nation}
                      className={`relative overflow-hidden border ${
                        t.titleHolder
                          ? 'bg-gradient-to-br from-[#3fced1]/15 to-zinc-900 border-[#3fced1]/40'
                          : 'bg-zinc-900/60 border-zinc-800'
                      }`}
                    >
                      <CardBody>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-4xl leading-none">{t.flag}</span>
                            <div className="min-w-0">
                              <p className="text-[11px] uppercase tracking-widest text-[#3fced1]">
                                Seed #{idx + 1}
                              </p>
                              <h4 className="text-xl font-extrabold text-white truncate">{t.nation}</h4>
                            </div>
                          </div>
                          {t.titleHolder && (
                            <Chip size="sm" className="bg-amber-500 text-black font-bold shrink-0">
                              Titelverteidiger
                            </Chip>
                          )}
                        </div>
                        <Divider className="my-3 bg-zinc-800" />
                        <div className="space-y-1.5">
                          <p className="text-sm text-gray-300">
                            <span className="text-gray-500">Spieler 1: </span>
                            <strong className="text-white">{t.player1}</strong>
                          </p>
                          <p className="text-sm text-gray-300">
                            <span className="text-gray-500">Spieler 2: </span>
                            <strong className="text-white">{t.player2}</strong>
                          </p>
                          {t.prevWinner && t.prevWinner.length > 0 && (
                            <p className="text-xs text-gray-500 pt-2">
                              <Trophy size={12} className="inline -mt-0.5 mr-1 text-amber-400" />
                              Sieger: {t.prevWinner.join(', ')}
                            </p>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Other teams */}
              <div>
                <SectionHeading
                  icon={<Globe2 size={22} />}
                  eyebrow="Gruppenphase"
                  title="Weitere starke Nationen"
                  description="Müssen sich über die Gruppenphase für das Achtelfinale qualifizieren."
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {NOTABLE_TEAMS.map((t) => (
                    <Card key={t.nation} className="bg-zinc-900/60 border border-zinc-800">
                      <CardBody className="flex flex-row items-center gap-3">
                        <span className="text-3xl">{t.flag}</span>
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-white truncate">{t.nation}</p>
                          <p className="text-xs text-gray-400 truncate">{t.player1}</p>
                          <p className="text-xs text-gray-400 truncate">{t.player2}</p>
                          {t.prevWinner && (
                            <p className="text-[10px] text-amber-400 mt-1">
                              <Trophy size={10} className="inline -mt-0.5 mr-1" />
                              Ex-Champion ({t.prevWinner[t.prevWinner.length - 1]})
                            </p>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </Tab>

          <Tab key="draw" title="Auslosung & Bracket">
            <div className="mt-6 space-y-8">
              <div>
                <SectionHeading
                  icon={<Users size={22} />}
                  eyebrow="Runde 1 · Gruppenphase"
                  title="Die 12 Gruppen im Überblick"
                  description="Jeweils nur der Gruppensieger qualifiziert sich für das Achtelfinale. Best of 7 Legs."
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {GROUP_STAGE.map((g) => (
                    <Card key={g.group} className="bg-zinc-900/60 border border-zinc-800">
                      <CardHeader className="pb-2">
                        <p className="font-bold text-[#3fced1] text-sm tracking-wider">{g.group}</p>
                      </CardHeader>
                      <CardBody className="pt-0 space-y-2">
                        {g.teams.map((team, idx) => (
                          <div
                            key={team.nation}
                            className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-zinc-950/60"
                          >
                            <span className="text-xs text-gray-500 w-4">{idx + 1}.</span>
                            <span className="text-xl">{team.flag}</span>
                            <span className="text-sm text-white font-medium truncate">{team.nation}</span>
                          </div>
                        ))}
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>

              <Divider className="bg-zinc-800" />

              <div>
                <SectionHeading
                  icon={<Trophy size={22} />}
                  eyebrow="Achtelfinale · Round of 16"
                  title="Bracket – Knockout-Phase"
                  description="Best of 15 Legs ab dem Achtelfinale. Im Finale wird auf Best of 19 erhöht."
                />
                <div className="overflow-x-auto pb-4">
                  <div className="grid grid-cols-4 gap-4 min-w-[800px]">
                    {/* Round of 16 */}
                    <div className="space-y-3">
                      <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
                        Achtelfinale
                      </p>
                      {ROUND16_BRACKET.map((pair, i) => (
                        <div
                          key={i}
                          className="rounded-xl border border-zinc-800 bg-zinc-900/60 overflow-hidden text-sm"
                        >
                          <div className="px-3 py-2 border-b border-zinc-800 text-gray-200">
                            {pair[0]}
                          </div>
                          <div className="px-3 py-2 text-gray-400">{pair[1]}</div>
                        </div>
                      ))}
                    </div>

                    {/* Quarter Finals */}
                    <div className="space-y-3 pt-8">
                      <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
                        Viertelfinale
                      </p>
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div
                          key={i}
                          className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden text-sm h-[88px] flex flex-col"
                        >
                          <div className="flex-1 px-3 py-2 border-b border-zinc-800 text-gray-500">
                            Sieger AF {i * 2 + 1}
                          </div>
                          <div className="flex-1 px-3 py-2 text-gray-500">
                            Sieger AF {i * 2 + 2}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Semi Finals */}
                    <div className="space-y-3 pt-24">
                      <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
                        Halbfinale
                      </p>
                      {Array.from({ length: 2 }).map((_, i) => (
                        <div
                          key={i}
                          className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden text-sm h-[200px] flex flex-col"
                        >
                          <div className="flex-1 px-3 py-2 border-b border-zinc-800 text-gray-500 flex items-center">
                            Sieger VF {i * 2 + 1}
                          </div>
                          <div className="flex-1 px-3 py-2 text-gray-500 flex items-center">
                            Sieger VF {i * 2 + 2}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Final */}
                    <div className="space-y-3 pt-56">
                      <p className="text-[11px] uppercase tracking-widest text-[#3fced1] mb-2 font-bold">
                        Finale
                      </p>
                      <div className="rounded-xl border-2 border-[#3fced1]/50 bg-gradient-to-br from-[#3fced1]/15 to-zinc-900 overflow-hidden text-sm h-[200px] flex flex-col">
                        <div className="flex-1 px-3 py-2 border-b border-[#3fced1]/30 text-white font-semibold flex items-center">
                          Sieger HF 1
                        </div>
                        <div className="flex-1 px-3 py-2 text-white font-semibold flex items-center">
                          Sieger HF 2
                        </div>
                        <div className="bg-[#3fced1] text-black text-center py-1.5 text-xs font-bold">
                          Best of 19 Legs
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Tab>

          <Tab key="schedule" title="Spielplan">
            <div className="mt-6">
              <SectionHeading
                icon={<Calendar size={22} />}
                eyebrow="11.–14. Juni 2026"
                title="Sessions & Uhrzeiten"
                description="Vier Spieltage in der Eissporthalle Frankfurt – insgesamt sieben Sessions inklusive Finale am Sonntagabend."
              />
              <div className="space-y-3">
                {SCHEDULE.map((s, idx) => (
                  <Card
                    key={idx}
                    className={`border ${
                      idx === SCHEDULE.length - 1
                        ? 'bg-gradient-to-r from-[#3fced1]/15 to-zinc-900 border-[#3fced1]/40'
                        : 'bg-zinc-900/60 border-zinc-800'
                    }`}
                  >
                    <CardBody className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex items-center gap-3 md:w-48 shrink-0">
                        <div
                          className={`h-12 w-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                            idx === SCHEDULE.length - 1
                              ? 'bg-[#3fced1] text-black'
                              : 'bg-zinc-800 text-[#3fced1]'
                          }`}
                        >
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-bold text-white">{s.day}</p>
                          <p className="text-xs text-gray-400">{s.date}</p>
                        </div>
                      </div>

                      <Divider orientation="vertical" className="hidden md:block h-12 bg-zinc-800" />

                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-white">{s.title}</p>
                        <p className="text-sm text-gray-400">{s.stage}</p>
                      </div>

                      <Chip
                        size="lg"
                        startContent={<Calendar size={14} className="ml-1" />}
                        className={
                          idx === SCHEDULE.length - 1
                            ? 'bg-[#3fced1] text-black font-bold'
                            : 'bg-zinc-800 text-white'
                        }
                      >
                        {s.time} Uhr
                      </Chip>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </div>
          </Tab>

          <Tab key="prize" title="Preisgeld">
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800">
                <CardHeader>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
                      Aufschlüsselung
                    </p>
                    <h3 className="text-xl font-bold">Preisgeld pro Runde</h3>
                  </div>
                </CardHeader>
                <CardBody className="pt-0">
                  <div className="space-y-2">
                    {PRIZE_BREAKDOWN.map((p, idx) => (
                      <div
                        key={p.stage}
                        className={`flex items-center justify-between gap-4 rounded-xl px-4 py-3 border ${
                          idx === 0
                            ? 'bg-gradient-to-r from-amber-500/20 to-zinc-900 border-amber-500/40'
                            : 'bg-zinc-950/60 border-zinc-800'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {idx === 0 && <Trophy size={20} className="text-amber-400" />}
                          <div>
                            <p className="font-bold text-white">{p.stage}</p>
                            {p.totalNote && (
                              <p className="text-xs text-gray-500">{p.totalNote}</p>
                            )}
                          </div>
                        </div>
                        <p
                          className={`text-lg font-extrabold ${
                            idx === 0 ? 'text-amber-300' : 'text-white'
                          }`}
                        >
                          {p.per}
                          <span className="text-xs font-medium text-gray-400 ml-1">/ Spieler</span>
                        </p>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500/20 via-zinc-900 to-zinc-950 border border-amber-500/30">
                <CardBody className="text-center py-10">
                  <Coins size={36} className="text-amber-300 mx-auto mb-2" />
                  <p className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold">
                    Gesamtpreisgeld 2026
                  </p>
                  <p className="text-5xl font-black text-white mt-1">£500.000</p>
                  <p className="text-sm text-gray-400 mt-3">
                    Erhöhung um £50.000 gegenüber 2025
                  </p>

                  <Divider className="my-6 bg-zinc-800" />

                  <div className="text-left space-y-3">
                    <div>
                      <p className="text-xs text-gray-400">Sieger-Team erhält</p>
                      <p className="text-2xl font-bold text-amber-300">£100.000</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Pro Spieler</p>
                      <p className="text-lg font-bold text-white">£50.000</p>
                    </div>
                  </div>

                  <Progress
                    aria-label="Preisgeldwachstum"
                    value={100}
                    classNames={{
                      base: 'mt-6',
                      indicator: 'bg-amber-400',
                      track: 'bg-zinc-800',
                    }}
                    size="sm"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    +233 % seit 2010 (£150.000)
                  </p>
                </CardBody>
              </Card>
            </div>
          </Tab>

          <Tab key="history" title="Vergangene Sieger">
            <div className="mt-6">
              <SectionHeading
                icon={<History size={22} />}
                eyebrow="Hall of Fame"
                title="Alle Sieger seit 2010"
                description="Seit 2019 hat jedes Jahr ein anderes Team gewonnen – ein Zeichen für die wachsende Dichte an der Weltspitze."
              />

              <div className="overflow-hidden rounded-2xl border border-zinc-800">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-900/80">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                        Jahr
                      </th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                        Sieger
                      </th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                        Spieler
                      </th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                        Finalist
                      </th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                        Ergebnis
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {PAST_WINNERS.map((w, idx) => (
                      <tr
                        key={w.year}
                        className={`border-t border-zinc-800 transition-colors hover:bg-zinc-900/60 ${
                          idx === 0 ? 'bg-amber-500/5' : ''
                        }`}
                      >
                        <td className="px-4 py-3 font-bold text-white">
                          {w.year}
                          {idx === 0 && (
                            <Chip
                              size="sm"
                              className="ml-2 bg-amber-500 text-black font-bold"
                            >
                              aktuell
                            </Chip>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{w.flag}</span>
                            <span className="font-bold text-white">{w.champion}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-300">{w.players}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-gray-400">
                            <span className="text-xl">{w.runnerUpFlag}</span>
                            <span>{w.runnerUp}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Chip
                            size="sm"
                            className="bg-zinc-800 text-[#3fced1] font-bold"
                          >
                            {w.score}
                          </Chip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Tab>
        </Tabs>
      </section>
    </div>
  );
};

export default WorldCupOfDartsPage;
